import React from "react";
import { Check, X, Minus, TrendingUp, Activity, Flame, Zap } from "lucide-react";
import { Reveal } from "./Reveal";

/** ----------------------------------------------------------
 *  Before / After — 4-week protocol timeline with progress bars
 *  ---------------------------------------------------------- */
const TIMELINE = [
  {
    label: "DAY 1 — BASELINE",
    desc: "Starting point. Energy, drive, recovery — wherever you are today.",
    metrics: { energy: 30, drive: 25, recovery: 35, confidence: 30 },
  },
  {
    label: "WEEK 2",
    desc: "Adaptogens online. You wake up sharper. Morning drive returns.",
    metrics: { energy: 55, drive: 50, recovery: 50, confidence: 50 },
  },
  {
    label: "WEEK 4",
    desc: "Compounded effect. Lifts feel heavier. Bedroom output up.",
    metrics: { energy: 75, drive: 75, recovery: 70, confidence: 78 },
  },
  {
    label: "WEEK 8",
    desc: "Full protocol. Lean strength, real stamina, undeniable presence.",
    metrics: { energy: 92, drive: 92, recovery: 88, confidence: 95 },
  },
];

export const BeforeAfterTimeline = () => (
  <section
    className="border-t border-white/10 py-20 md:py-28 grain relative"
    data-testid="before-after-section"
  >
    <div
      className="absolute inset-0 opacity-25 pointer-events-none"
      style={{
        background:
          "radial-gradient(ellipse 60% 50% at 10% 20%, rgba(209,0,0,0.3), transparent 60%)",
      }}
    />
    <div className="relative max-w-[1400px] mx-auto px-6 md:px-12">
      <Reveal>
        <div className="font-display text-xs tracking-[0.3em] text-[#D10000] mb-4">
          THE COMPOUNDED EFFECT
        </div>
        <h2
          className="font-display uppercase leading-[0.9] tracking-tight"
          style={{ fontSize: "clamp(2.25rem, 5vw, 4.5rem)" }}
        >
          Day 1 → <span className="text-stroke">Week 8.</span>
        </h2>
        <p className="mt-6 max-w-xl text-white/65 leading-relaxed">
          Supplements aren't magic. They reward consistency. Here's what the
          Primenax protocol looks like over 8 weeks of daily use.
        </p>
      </Reveal>

      <div className="mt-14 grid grid-cols-1 md:grid-cols-4 gap-px bg-white/5">
        {TIMELINE.map((t, i) => (
          <Reveal key={i} delay={i * 0.08}>
            <div className="bg-[#0A0A0A] p-6 md:p-8 h-full">
              <div className="font-display text-xs tracking-[0.3em] text-[#D10000] mb-3">
                {String(i + 1).padStart(2, "0")} · {t.label}
              </div>
              <p className="text-white/70 text-sm leading-relaxed min-h-[64px]">
                {t.desc}
              </p>
              <div className="mt-6 space-y-3" data-testid={`timeline-stage-${i}`}>
                {Object.entries(t.metrics).map(([k, v]) => (
                  <div key={k}>
                    <div className="flex items-center justify-between text-[10px] tracking-[0.25em] uppercase text-white/50 mb-1.5">
                      <span>{k}</span>
                      <span className="text-white/80">{v}%</span>
                    </div>
                    <div className="h-1 bg-white/5 overflow-hidden">
                      <div
                        className="h-full bg-[#D10000] transition-all duration-700"
                        style={{ width: `${v}%` }}
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </Reveal>
        ))}
      </div>
      <p className="mt-6 text-white/35 text-xs max-w-2xl">
        * Illustrative subjective scoring across daily users following the
        Primenax protocol. Individual results vary. Not a clinical claim.
      </p>
    </div>
  </section>
);

/** ----------------------------------------------------------
 *  Comparison Table — Primenax vs Generic Competitors
 *  ---------------------------------------------------------- */
const ROWS = [
  { label: "Naturally sourced ingredients", us: "yes", them: "partial" },
  { label: "Clinically-studied doses (no underdosing)", us: "yes", them: "no" },
  { label: "Full ingredient disclosure (no proprietary blends)", us: "yes", them: "no" },
  { label: "Third-party lab tested for purity & potency", us: "yes", them: "no" },
  { label: "cGMP-certified manufacturing (USA)", us: "yes", them: "partial" },
  { label: "No artificial fillers or dyes", us: "yes", them: "no" },
  { label: "Designed specifically for male physiology", us: "yes", them: "no" },
  { label: "Stackable system (drive + testosterone)", us: "yes", them: "no" },
  { label: "30-day money-back guarantee", us: "yes", them: "partial" },
];

const Cell = ({ state, accent }) => {
  const map = {
    yes: {
      icon: Check,
      cls: accent
        ? "bg-[#D10000] text-white"
        : "bg-white/5 text-emerald-400",
      label: "Yes",
    },
    no: { icon: X, cls: "bg-white/5 text-white/35", label: "No" },
    partial: {
      icon: Minus,
      cls: "bg-white/5 text-amber-300",
      label: "Partial",
    },
  };
  const { icon: Icon, cls, label } = map[state];
  return (
    <div className={`flex items-center justify-center gap-2 py-4 px-2 ${cls}`}>
      <Icon className="h-4 w-4" />
      <span className="font-display text-xs tracking-[0.25em] uppercase">
        {label}
      </span>
    </div>
  );
};

export const CompetitorComparison = () => (
  <section
    className="border-t border-white/10 py-20 md:py-28"
    data-testid="comparison-section"
  >
    <div className="max-w-[1400px] mx-auto px-6 md:px-12">
      <Reveal>
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 mb-12">
          <div className="lg:col-span-7">
            <div className="font-display text-xs tracking-[0.3em] text-[#D10000] mb-4">
              HOW WE STACK UP
            </div>
            <h2
              className="font-display uppercase leading-[0.9] tracking-tight"
              style={{ fontSize: "clamp(2.25rem, 5vw, 4.5rem)" }}
            >
              Primenax vs <br /> <span className="text-stroke">the rest.</span>
            </h2>
          </div>
          <div className="lg:col-span-5 flex items-end">
            <p className="text-white/65 max-w-md leading-relaxed">
              Walk into any drugstore and you'll find a wall of male
              supplements. Almost none disclose their doses. Almost none third-
              party test. Here's the cold side-by-side.
            </p>
          </div>
        </div>
      </Reveal>

      <Reveal>
        <div className="border border-white/10 overflow-x-auto">
          {/* Header */}
          <div className="grid grid-cols-[1.6fr_1fr_1fr] bg-[#0A0A0A] border-b border-white/10">
            <div className="px-6 py-5 font-display text-xs tracking-[0.3em] text-white/40">
              CRITERIA
            </div>
            <div className="px-4 py-5 text-center bg-[#D10000]/15 border-x border-white/10">
              <div className="font-display text-lg tracking-tight uppercase text-[#D10000]">
                Primenax
              </div>
            </div>
            <div className="px-4 py-5 text-center">
              <div className="font-display text-lg tracking-tight uppercase text-white/60">
                Generic Brand
              </div>
            </div>
          </div>

          {/* Rows */}
          {ROWS.map((r, i) => (
            <div
              key={i}
              className={`grid grid-cols-[1.6fr_1fr_1fr] items-stretch ${
                i % 2 === 0 ? "bg-[#070707]" : "bg-[#0A0A0A]"
              }`}
              data-testid={`compare-row-${i}`}
            >
              <div className="px-6 py-5 text-white/85 text-sm md:text-base flex items-center">
                {r.label}
              </div>
              <div className="border-x border-white/5">
                <Cell state={r.us} accent />
              </div>
              <div>
                <Cell state={r.them} />
              </div>
            </div>
          ))}
        </div>
      </Reveal>
    </div>
  </section>
);

/** ----------------------------------------------------------
 *  By the Numbers — stat callouts
 *  ---------------------------------------------------------- */
const STATS = [
  { icon: TrendingUp, value: "92%", label: "Reported improved morning drive within 4 weeks" },
  { icon: Activity, value: "87%", label: "Reported faster recovery between workouts" },
  { icon: Flame, value: "94%", label: "Said confidence noticeably increased" },
  { icon: Zap, value: "100%", label: "Naturally-formulated ingredients — zero filler" },
];

export const BenefitsStats = () => (
  <section
    className="border-t border-white/10 py-20 md:py-28 bg-[#070707]"
    data-testid="stats-section"
  >
    <div className="max-w-[1400px] mx-auto px-6 md:px-12">
      <Reveal>
        <div className="font-display text-xs tracking-[0.3em] text-[#D10000] mb-4">
          BY THE NUMBERS
        </div>
        <h2
          className="font-display uppercase leading-[0.9] tracking-tight"
          style={{ fontSize: "clamp(2.25rem, 5vw, 4.5rem)" }}
        >
          Why men <br /> <span className="text-stroke">come back.</span>
        </h2>
      </Reveal>

      <div className="mt-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-px bg-white/5">
        {STATS.map((s, i) => (
          <Reveal key={i} delay={i * 0.08}>
            <div className="bg-[#0A0A0A] p-8 h-full">
              <s.icon className="h-7 w-7 text-[#D10000] mb-6" />
              <div
                className="font-display tracking-tight leading-none"
                style={{ fontSize: "clamp(2.5rem, 5vw, 4rem)" }}
              >
                {s.value}
              </div>
              <p className="text-white/65 mt-4 leading-relaxed text-sm">
                {s.label}
              </p>
            </div>
          </Reveal>
        ))}
      </div>
      <p className="mt-6 text-white/35 text-xs max-w-2xl">
        * Self-reported customer feedback from voluntary post-purchase survey.
        Individual results vary.
      </p>
    </div>
  </section>
);
